import { column, edge, flow, makeFlow, node } from "../builders";
import type { TemplateDef } from "../types";

const join = column("f1", 100, 70, 92, [
  ["t1", "auto-trigger", "Notify-me submitted", { props: { triggerType: "Form Submitted", event: "Back-in-stock form" } }],
  ["e1", "auto-email", "We'll tell you first", { props: { subject: "You're on the list for {{contact.wanted_sku}}" } }],
  ["tg1", "auto-tag-add", "Tag: waiting-on-restock", { props: { tag: "waiting-on-restock" } }],
]);

const blast = column("f2", 100, 70, 92, [
  ["wh", "auto-webhook", "Inventory: back in stock", { props: { sourceSystem: "Shopify / your inventory system" } }],
  ["s1", "auto-sms", "It's back — 24h early access", { props: { message: "{{contact.wanted_sku}} is back. Your early link: {{link.product}}" } }],
  ["e2", "auto-email", "Back in stock (and going fast)", { props: { subject: "It's back — you get first go" } }],
  ["w1", "auto-wait", "Wait 6 hours", { props: { duration: "6", unit: "hours" } }],
  ["gl", "auto-goal", "Goal: they bought", { props: { goalType: "Purchase Made", goal: "Any order of the restocked SKU" } }],
  ["e3", "auto-email", "Last call", { props: { subject: "Early access ends tonight" } }],
  ["tg2", "auto-tag-remove", "Clear the waiting tag", { props: { tag: "waiting-on-restock" } }],
]);

const def: TemplateDef = {
  slug: "back-in-stock-waitlist",
  name: "Back-in-Stock Waitlist",
  category: "E-commerce",
  tagline:
    "Turn a sold-out page into a list, then convert it in the six hours that matter.",
  description:
    "A free back-in-stock template: a notify-me form on the sold-out product page builds a waiting list, and an inventory webhook fires an SMS-first early-access blast with a goal step that pulls buyers out the moment they order. Clone it into FrazzleCRM and stop losing sold-out traffic.",
  whatsInside: [
    "Notify-me form on the sold-out page, wired to a waiting tag",
    "Inbound webhook entry for your inventory or store restock event",
    "SMS-first blast with a six-hour early-access window",
    "A Goal step so buyers skip the last-call email automatically",
  ],
  flow: makeFlow(
    [
      node("src1", "traffic-seo", "SEO / Organic", 40, 60, { props: { keywords: "product name + in stock" } }),
      node("sp", "page-sales", "Sold-out product page", 360, 140, { props: { price: "$120" } }),
      node("pp", "page-popup", "Notify me when it's back", 360, 320, { props: { trigger: "Out-of-stock button" } }),
      flow("f1", "Join the Waitlist", 680, 40, 420, 396),
      ...join.nodes,
      node("inv", "prov-shopify", "Inventory restocked", 680, 500),
      flow("f2", "Restock Blast", 1180, 40, 420, 764),
      ...blast.nodes,
      node("co", "page-checkout", "Checkout", 1700, 260, { props: { price: "$120" } }),
      node("out", "outcome-sale", "Recovered sale", 2020, 260, { props: { value: "$120" } }),
      node("n1", "util-note", "SMS first, email second", 40, 320, {
        notes:
          "Restocks are a race. The text is what gets opened inside the window; the email is for everyone whose number you never got.",
      }),
    ],
    [
      edge("src1", "sp"),
      edge("sp", "pp"),
      edge("pp", "f1"),
      ...join.edges,
      edge("f1", "inv"),
      edge("inv", "f2"),
      ...blast.edges,
      edge("f2", "co"),
      edge("co", "out"),
    ]
  ),
};

export default def;
